import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pencil, Check, ChevronLeft, ChevronRight, Sparkles, Wand2, Crown, Glasses, Smile, Waves, Hammer, Swords, Sun, Anchor } from 'lucide-react';

// ─── Shark Data Configuration ───
export interface CostumeConfig {
  id: string;
  name: string;
  icon: React.ReactNode;
  sideView: string;
  frontView: string;
}

export interface SharkConfig {
  id: string;
  species: string;
  defaultName: string;
  icon: React.ReactNode;
  personality: string;
  costumes: CostumeConfig[];
}

export const SHARK_SPECIES: SharkConfig[] = [
  {
    id: 'blue_shark',
    species: 'Blue Shark',
    defaultName: 'Finn',
    icon: <Waves size={14} />,
    personality: 'Swift & Strategic',
    costumes: [
      { id: 'default', name: 'Default', icon: <Sparkles size={16} />, sideView: '/models/shark_masckot/blue shark/side view/Blueshark.svg', frontView: '/models/shark_masckot/blue shark/front view/bluesharkfront.svg' },
      { id: 'crown', name: 'Crown', icon: <Crown size={16} />, sideView: '/models/shark_masckot/blue shark/side view/bluesharkcrown.svg', frontView: '/models/shark_masckot/blue shark/front view/bluesharkfrontcrown.svg' },
      { id: 'glasses', name: 'Glasses', icon: <Glasses size={16} />, sideView: '/models/shark_masckot/blue shark/side view/bluesharkglass.svg', frontView: '/models/shark_masckot/blue shark/front view/bluesharkglasses.svg' },
    ]
  },
  {
    id: 'hammerhead',
    species: 'Hammerhead',
    defaultName: 'Hammer',
    icon: <Hammer size={14} />,
    personality: 'Bold & Decisive',
    costumes: [
      { id: 'default', name: 'Default', icon: <Sparkles size={16} />, sideView: '/models/shark_masckot/hammerhead shark/side view/Hammerhead.svg', frontView: '/models/shark_masckot/hammerhead shark/front view/hammerheadfront.svg' },
      { id: 'crown', name: 'Crown', icon: <Crown size={16} />, sideView: '/models/shark_masckot/hammerhead shark/side view/hammerheadcrown.svg', frontView: '/models/shark_masckot/hammerhead shark/front view/hammerheadfrontcrown.svg' },
      { id: 'glasses', name: 'Glasses', icon: <Glasses size={16} />, sideView: '/models/shark_masckot/hammerhead shark/side view/hammerheadglasses.svg', frontView: '/models/shark_masckot/hammerhead shark/front view/hammerheadfrontglass.svg' },
    ]
  },
  {
    id: 'saw_shark',
    species: 'Saw Shark',
    defaultName: 'Razor',
    icon: <Swords size={14} />,
    personality: 'Sharp & Precise',
    costumes: [
      { id: 'default', name: 'Default', icon: <Sparkles size={16} />, sideView: '/models/shark_masckot/japanese saw shark/side view/sawshark.svg', frontView: '/models/shark_masckot/japanese saw shark/front view/SawSharkfront.svg' },
      { id: 'crown', name: 'Crown', icon: <Crown size={16} />, sideView: '/models/shark_masckot/japanese saw shark/side view/sawsharkcrown.svg', frontView: '/models/shark_masckot/japanese saw shark/front view/sawfrontcrown.svg' },
      { id: 'glasses', name: 'Glasses', icon: <Glasses size={16} />, sideView: '/models/shark_masckot/japanese saw shark/side view/sawsharkglasses.svg', frontView: '/models/shark_masckot/japanese saw shark/front view/sawfrontglass.svg' },
    ]
  },
  {
    id: 'lemon_shark',
    species: 'Lemon Shark',
    defaultName: 'Zest',
    icon: <Sun size={14} />,
    personality: 'Friendly & Warm',
    costumes: [
      { id: 'default', name: 'Default', icon: <Sparkles size={16} />, sideView: '/models/shark_masckot/lemon shark/side view/LemonShark.svg', frontView: '/models/shark_masckot/lemon shark/front view/lemonsharkfront.svg' },
      { id: 'crown', name: 'Crown', icon: <Crown size={16} />, sideView: '/models/shark_masckot/lemon shark/side view/lemoncrown.svg', frontView: '/models/shark_masckot/lemon shark/front view/lemonfrontcrown.svg' },
      { id: 'glasses', name: 'Glasses', icon: <Glasses size={16} />, sideView: '/models/shark_masckot/lemon shark/side view/lemonglasses.svg', frontView: '/models/shark_masckot/lemon shark/front view/lemonfrontglass.svg' },
      { id: 'mustache', name: 'Mustache', icon: <Smile size={16} />, sideView: '/models/shark_masckot/lemon shark/side view/lemonsharkmustache.svg', frontView: '/models/shark_masckot/lemon shark/front view/lemonfrontmustache.svg' },
    ]
  },
  {
    id: 'whale_shark',
    species: 'Whale Shark',
    defaultName: 'Atlas',
    icon: <Anchor size={14} />,
    personality: 'Calm & Powerful',
    costumes: [
      { id: 'default', name: 'Default', icon: <Sparkles size={16} />, sideView: '/models/shark_masckot/whale shark/side view/whale.svg', frontView: '/models/shark_masckot/whale shark/front view/whalefrontsvg.svg' },
      { id: 'crown', name: 'Crown', icon: <Crown size={16} />, sideView: '/models/shark_masckot/whale shark/side view/whalecrown.svg', frontView: '/models/shark_masckot/whale shark/front view/whalefrontcrown.svg' },
      { id: 'glasses', name: 'Glasses', icon: <Glasses size={16} />, sideView: '/models/shark_masckot/whale shark/side view/whaleglasses.svg', frontView: '/models/shark_masckot/whale shark/front view/whalefrontglass.svg' },
    ]
  },
];

export const SHARK_COLORS = [
  { id: 'default', hex: 'transparent', name: 'Default' },
  { id: 'ocean', hex: '#3E8FA6', name: 'Ocean Blue' },
  { id: 'emerald', hex: '#10B981', name: 'Emerald' },
  { id: 'amber', hex: '#F59E0B', name: 'Sunset' },
  { id: 'purple', hex: '#8B5CF6', name: 'Royal Purple' },
  { id: 'crimson', hex: '#EF4444', name: 'Crimson Red' },
];

// ─── Props ───
interface SharkAvatarSelectorProps {
  selectedSpecies: string;
  sharkColor: string;
  sharkCostume?: string;
  onSpeciesChange: (speciesId: string) => void;
  onCostumeChange?: (costumeId: string) => void;
  variant?: 'signup' | 'dashboard';
  children?: React.ReactNode;
}

export function SharkAvatarSelector({
  selectedSpecies,
  sharkColor,
  sharkCostume = 'default',
  onSpeciesChange,
  onCostumeChange,
  variant = 'signup',
  children,
}: SharkAvatarSelectorProps) {
  const [direction, setDirection] = useState(0);
  const currentIndex = SHARK_SPECIES.findIndex(s => s.id === selectedSpecies);
  const currentShark = SHARK_SPECIES[currentIndex] || SHARK_SPECIES[0];

  const currentCostumeObj = currentShark.costumes.find(c => c.id === sharkCostume) || currentShark.costumes[0];

  const navigate = (dir: number) => {
    setDirection(dir);
    const nextIndex = (currentIndex + dir + SHARK_SPECIES.length) % SHARK_SPECIES.length;
    const nextShark = SHARK_SPECIES[nextIndex];
    onSpeciesChange(nextShark.id);
    
    // Automatically reset costume if the new shark doesn't have the selected costume
    if (onCostumeChange && !nextShark.costumes.some(c => c.id === sharkCostume)) {
      onCostumeChange('default');
    }
  };

  const isDash = variant === 'dashboard';

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .shark-selector-wrapper {
            flex-direction: column !important;
            align-items: center !important;
          }
          .shark-selector-left, .shark-selector-right {
            flex: none !important;
            width: 100% !important;
            max-width: 100% !important;
          }
        }
      `}</style>
      <div 
        className="shark-selector-wrapper"
        style={{
          display: 'flex', 
          flexDirection: isDash ? 'column' : 'row', 
          gap: isDash ? '28px' : '12px',
          alignItems: 'flex-start',
          width: '100%',
        }}>
        {/* ─── Left Column (Shark Display + Info) ─── */}
        <div className="shark-selector-left" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '16px', 
          flex: isDash ? 'none' : '1',
          background: 'transparent',
          border: isDash ? 'none' : '1px solid rgba(255, 255, 255, 0.08)',
          padding: isDash ? '0' : '8px 0 8px',
          alignItems: 'center'
        }}>
        {/* ─── Shark Display ─── */}
        <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
      }}>
        {/* Prev Button */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            width: '32px', height: '32px', borderRadius: '50%', border: 'none',
            background: isDash ? 'var(--admin-card-bg)' : 'rgba(255, 255, 255, 0.06)',
            boxShadow: isDash ? '0 2px 10px rgba(0,0,0,0.12)' : 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: isDash ? 'var(--admin-card-text)' : '#ffffff',
            zIndex: 2, flexShrink: 0,
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = isDash ? 'var(--admin-card-bg)' : 'rgba(255, 255, 255, 0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = isDash ? 'var(--admin-card-bg)' : 'rgba(255, 255, 255, 0.06)'; }}
        >
          <ChevronLeft size={16} />
        </button>

        {/* Shark Preview Area */}
        <div style={{
          position: 'relative',
          width: isDash ? '220px' : '140px', height: isDash ? '200px' : '120px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {/* Background glow using fixed theme color */}
          {isDash && (
            <div style={{
              position: 'absolute',
              width: '180px', height: '180px',
              borderRadius: '50%',
              background: `radial-gradient(circle, #38bdf822 0%, transparent 70%)`,
              filter: 'blur(20px)',
            }} />
          )}

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={`${currentShark.id}-${currentCostumeObj.id}`}
              custom={direction}
              initial={{ opacity: 0, x: direction > 0 ? 80 : -80, scale: 0.85 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: direction > 0 ? -80 : 80, scale: 0.85 }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
              }}
            >
              <div style={{ position: 'relative', width: isDash ? '200px' : '120px', height: isDash ? '170px' : '100px' }}>
                <motion.img
                  src={currentCostumeObj.sideView}
                  alt={`${currentShark.species} - ${currentCostumeObj.name}`}
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.15))',
                  }}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div style={{
                  position: 'absolute', inset: 0,
                  backgroundColor: sharkColor,
                  mixBlendMode: 'color',
                  opacity: 0.6,
                  WebkitMaskImage: `url('${currentCostumeObj.sideView}')`,
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                }} 
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Next Button */}
        <button
          type="button"
          onClick={() => navigate(1)}
          style={{
            width: '32px', height: '32px', borderRadius: '50%', border: 'none',
            background: isDash ? 'var(--admin-card-bg)' : 'rgba(255, 255, 255, 0.06)',
            boxShadow: isDash ? '0 2px 10px rgba(0,0,0,0.12)' : 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: isDash ? 'var(--admin-card-text)' : '#ffffff',
            zIndex: 2, flexShrink: 0,
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = isDash ? 'var(--admin-card-bg)' : 'rgba(255, 255, 255, 0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = isDash ? 'var(--admin-card-bg)' : 'rgba(255, 255, 255, 0.06)'; }}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* ─── Species Info ─── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentShark.id + '-info'}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          style={{ textAlign: 'center', marginTop: '-4px' }}
        >
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '4px 12px', borderRadius: '4px',
            background: isDash ? `#38bdf815` : '#D9D9D9',
            border: isDash ? `1px solid #38bdf830` : 'none',
            marginBottom: '4px',
          }}>
            <span style={{
              fontSize: '11px', fontWeight: 600, 
              color: isDash ? '#38bdf8' : '#000000',
              fontFamily: 'var(--font-body)',
              letterSpacing: '0.02em',
            }}>
              {currentShark.species}
            </span>
          </div>
          <p style={{
            fontSize: '11px', fontWeight: 400, marginTop: '4px',
            color: isDash ? 'var(--admin-card-subtext)' : 'rgba(255, 255, 255, 0.55)',
            fontFamily: 'var(--font-body)',
          }}>
            {currentShark.personality}
          </p>
        </motion.div>
      </AnimatePresence>

        {/* ─── Species Dot Indicators ─── */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '6px'
        }}>
          {SHARK_SPECIES.map((shark, i) => (
            <button
              type="button"
              key={shark.id}
              onClick={() => {
                setDirection(i > currentIndex ? 1 : -1);
                onSpeciesChange(shark.id);
                if (onCostumeChange && !shark.costumes.some(c => c.id === sharkCostume)) {
                  onCostumeChange('default');
                }
              }}
              style={{
                width: i === currentIndex ? '16px' : '6px',
                height: '6px',
                borderRadius: '3px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: i === currentIndex ? '#00cfcf' : (isDash ? 'var(--admin-card-border)' : 'rgba(255, 255, 255, 0.12)'),
                padding: 0
              }}
              title={shark.species}
            />
          ))}
        </div>
      </div>

      {/* ─── Right Column (Accessories + Children) ─── */}
      <div className="shark-selector-right" style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: isDash ? 'none' : 1 }}>
        {/* ─── Accessories / Costumes Picker ─── */}
        {onCostumeChange && currentShark.costumes.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{
              fontSize: '11px', fontWeight: 600,
              color: isDash ? 'var(--admin-card-text)' : '#869CA7',
              display: 'flex', alignItems: 'center', gap: '6px',
              textTransform: 'uppercase', letterSpacing: '0.05em'
            }}>
              <Wand2 size={13} />
              Accessories
            </label>
            <div style={{ display: 'flex', flexDirection: isDash ? 'row' : 'column', gap: '8px', flexWrap: 'wrap' }}>
              {currentShark.costumes.map(costume => {
                const isSelected = sharkCostume === costume.id;
                
                // If dashboard, keep the old design with images
                if (isDash) {
                  return (
                    <button
                      key={costume.id}
                      type="button"
                      onClick={() => onCostumeChange(costume.id)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                        padding: '8px 12px', borderRadius: '12px', border: '1.5px solid',
                        cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        background: isSelected ? `#38bdf815` : 'var(--admin-input-bg, transparent)',
                        borderColor: isSelected ? '#38bdf8' : 'var(--admin-card-border)',
                        boxShadow: isSelected ? `0 2px 8px #38bdf830` : 'none',
                        minWidth: '76px',
                      }}
                    >
                      <div style={{ position: 'relative', width: '40px', height: '40px' }}>
                        <img 
                          src={costume.frontView} 
                          alt={costume.name} 
                          style={{ 
                            width: '100%', height: '100%', 
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                          }} 
                        />
                        <div style={{
                          position: 'absolute', inset: 0,
                          backgroundColor: sharkColor,
                          mixBlendMode: 'color',
                          opacity: 0.6,
                          WebkitMaskImage: `url('${costume.frontView}')`,
                          WebkitMaskSize: 'contain',
                          WebkitMaskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                        }} />
                      </div>
                      <span style={{
                        fontSize: '11px', fontWeight: 600,
                        color: isSelected ? '#38bdf8' : 'var(--admin-card-subtext)',
                      }}>
                        {costume.name}
                      </span>
                    </button>
                  );
                }

                // If signup variant, use the text-only vertical buttons
                return (
                  <button
                    key={costume.id}
                    type="button"
                    onClick={() => onCostumeChange(costume.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '6px', border: '1px solid',
                      cursor: 'pointer', transition: 'all 0.2s',
                      background: isSelected ? 'rgba(255, 255, 255, 0.05)' : '#020A15',
                      borderColor: isSelected ? 'rgba(0, 207, 207, 0.3)' : 'rgba(255, 255, 255, 0.08)',
                      width: '100%',
                    }}
                  >
                    <span style={{
                      fontSize: '12px', fontWeight: 500, fontFamily: 'var(--font-body)',
                      color: isSelected ? '#00cfcf' : '#869CA7',
                    }}>
                      {costume.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {isDash && children}
      </div>

    </div>
    
    {!isDash && (
      <div style={{ marginTop: '8px' }}>
        {children}
      </div>
    )}
    </>
  );
}

